import { useEffect } from 'react';
import Layout from '../../../../../components/layout/Layout';
import Breadcrumb from '../../../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../../../utils/seo';

// ===== Team Leadership Training (EN) full redesign (Implementer C scope) =====
// Design base: home / about/overview new style. Data unchanged; canonical EN copy from prior working tree only.

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

const OVERVIEW = [
  'The Head Butler oversees multiple butlers and staff, responsible for maintaining service quality throughout the estate. Managing diverse tasks with limited personnel while calmly handling unexpected situations—these management methods contain many elements that modern corporate leaders should learn.',
  "This training teaches butler leadership from both theoretical and practical perspectives. You'll learn servant leadership principles, team composition and task distribution, effective instruction methods, subordinate development, early conflict detection and resolution, and emergency decision-making processes—all directly applicable to actual work. Through group work and role-playing, you'll acquire practical leadership skills.",
  'This training is ideal for hotel and ryokan managers, restaurant managers, luxury service industry leaders, and anyone in a leadership position.',
];

const FEATURES = [
  { icon: 'ri-lightbulb-line', t: 'Practical Theory', d: 'Theory based on butler field experience.' },
  { icon: 'ri-team-line', t: 'Group Work', d: 'Learn through practical exercises.' },
  { icon: 'ri-rocket-line', t: 'Immediate Application', d: 'Skills you can use from tomorrow.' },
];

const CURRICULUM = [
  { part: 'Part 1', time: '45 min', title: 'Fundamentals of Leadership', desc: 'Learn the definition of leadership in butlering, servant leadership principles, the difference between authority and trust, and qualities required of leaders. Understand the essence of "supportive" leadership practiced by Head Butlers and learn how to apply it in modern corporate organizations. Through not only theory but also actual butler field cases, deeply learn effective leadership approaches.' },
  { part: 'Part 2', time: '45 min', title: 'Team Building', desc: 'Master effective team composition methods, identifying member strengths, task distribution optimization, and techniques to enhance team cohesion. Learn butler-practiced "right person in the right place" personnel placement, management methods to maximize individual abilities, and specific ways to improve overall team performance. Acquire practical skills through group work.' },
  { part: 'Part 3', time: '45 min', title: 'Effective Communication', desc: 'Learn how to give clear instructions, listening techniques and feedback, utilizing non-verbal communication, and establishing reporting/liaison/consultation systems. Master butler-practiced "empathetic" communication techniques, dialogue methods that build trust, and effective information-sharing mechanisms to strengthen team collaboration.' },
  { part: 'Part 4', time: '45 min', title: 'Conflict Resolution and Case Studies', desc: 'Develop practical skills through early conflict detection, promoting constructive discussion, mediation and problem-solving techniques, and exercises using actual cases. Learn butler-practiced "calm judgment" and "fair coordination" techniques, methods to turn emotional conflicts into constructive discussions, and approaches to transform team problems into growth opportunities. Acquire field-ready problem-solving skills through actual case studies.' },
];

const RECOMMENDS = [
  'Luxury hotel & ryokan managers',
  'Restaurant managers & store managers',
  'Concierge department heads',
  'Luxury service industry team leaders',
  'New managers in hospitality',
  'Those managing diverse staff',
  'Departments facing team collaboration challenges',
  'Organizations aiming to improve service quality',
];

const OUTCOMES = [
  { t: 'Practical Leadership Skills', d: 'Acquire not only theory but specific methods immediately applicable in the field. Master practical leadership techniques based on butler field experience.' },
  { t: 'Enhanced Team Management', d: 'Master techniques to maximize member abilities and enhance overall team performance. Learn optimal personnel placement and effective task distribution.' },
  { t: 'Strengthened Problem-Solving', d: 'Develop the ability to resolve conflicts constructively and turn them into team growth. Master calm judgment and fair coordination techniques.' },
  { t: 'Improved Communication Skills', d: 'Master clear instruction, accurate feedback, and effective listening techniques. Learn dialogue methods that build trust.' },
];

const SPECS = [
  { k: 'Training Duration', v: '3 hours per day (morning/afternoon available)' },
  { k: 'Participants', v: '10-20 people' },
  { k: 'Target Level', v: 'Intermediate to Advanced (Managers & Leaders)' },
  { k: 'Location', v: "Your company's meeting room or our association venue" },
  { k: 'Customization', v: 'Content adjustment available (separate consultation)' },
];

const INCLUDES = ['Training program implementation', 'Textbook materials (for all participants)', 'Certificates (for all participants)', 'Instructor transportation (within Tokyo)', 'Post-training follow-up (1 session)'];

const FLOW = [
  { t: 'Inquiry & Information Request', d: 'Please feel free to contact us by phone or web form. We will send you detailed training materials.' },
  { t: 'Consultation & Proposal', d: "Please share your company's challenges and requirements. We will customize and propose the optimal training program." },
  { t: 'Quotation & Contract', d: 'We will provide a detailed quotation based on the training content. Once approved, we will proceed with the contract.' },
  { t: 'Preparation & Coordination', d: 'We will conduct detailed meetings for venue confirmation, participant coordination, material preparation, and other training implementation details.' },
  { t: 'Training Implementation', d: "Experienced instructors will conduct high-quality training, flexibly adapting to participants' understanding levels." },
  { t: 'Follow-up', d: 'Post-training surveys, effectiveness measurement, and ongoing support to help establish training results.' },
];

const RELATED = [
  { label: 'Communication Skills Training', href: '/en/seminars/corporate/communication-skills', desc: 'Learn listening, expression, and non-verbal communication techniques from butlers' },
  { label: 'Crisis Management Training', href: '/en/seminars/corporate/crisis-management', desc: 'Learn risk assessment and emergency response techniques from butlers' },
  { label: 'Corporate Training Programs List', href: '/en/seminars', desc: 'View all training programs and find the optimal combination' },
];

export default function EnTeamLeadershipPage() {
  useEffect(() => {
    document.title = 'Team Leadership Training | Leadership Development | Japan Butler Association';
    updateSEO({
      title: 'Team Leadership Training | Leadership Development | Japan Butler Association',
      description: "Japan Butler Association's Team Leadership Training. Learn effective leadership skills and team management abilities to improve organizational strength through our corporate training program.",
      canonical: 'https://butler-association.jp/en/seminars/corporate/team-leadership',
      hreflang: { ja: 'https://butler-association.jp/seminars/corporate/team-leadership', en: 'https://butler-association.jp/en/seminars/corporate/team-leadership' },
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Seminars', href: '/en/seminars' },
    { label: 'Team Leadership Training' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-corp-team-leadership.jpg" alt="Team Leadership Training with a butler instructing participants" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[760px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Team Leadership</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.4] tracking-[0.04em] mb-6">Team Leadership Training</h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              Team Leadership Training is a practical program that applies the team management methods practiced by butlers for years to modern corporate organizations. The Head Butler oversees multiple staff members and manages the service of the entire estate. Leadership skills for achieving the best results with limited resources can be directly applied to modern corporate management. This training systematically covers leadership theory, team building, effective communication, and conflict resolution—all practical skills necessary for management positions.
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-10">
          <p className="border-l-2 border-[#a38e65] bg-[#f8f6f0] px-5 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
            ⚠️ This training aims at skill acquisition and does not guarantee performance improvement or promotion.
          </p>
        </div>
      </div>

      <NavyBand no="I" ja="About This Training" en="Overview" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start mb-12 sm:mb-14">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-6">
                The Head Butler's <span className="text-[#a38e65]">supportive leadership</span>
              </h3>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                {OVERVIEW.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <img src="/images/generated/jba-space-seminar-01.jpg" alt="A training salon hall filled with morning light" className="w-full h-64 lg:h-[380px] object-cover border border-[#a38e65]/30" />
          </div>
          <div className="grid sm:grid-cols-3 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] sm:border-b-0 sm:border-r last:border-r-0 py-7 sm:px-6 first:sm:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New variation 1 (numbered time-badge curriculum). Origin: home IV full-width editorial row. */}
      <NavyBand no="II" ja="Training Curriculum (3 hours/day)" en="Curriculum" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              Team Leadership Training systematically covers four areas based on butler field experience. Each section consists of practical content, allowing you to acquire not only knowledge but also actual management techniques.
            </p>
          </div>
          <img src="/images/generated/jba-fs-225-seminars_corporate_team-leadership-v5.jpg" alt="Team Leadership Training curriculum materials and lecture" className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10" />
          <div className="border-t-2 border-[#1a2942]">
            {CURRICULUM.map((c) => (
              <div key={c.part} className="flex flex-col md:flex-row gap-4 md:gap-10 border-b border-[#e2ddd2] py-7">
                <div className="md:w-[220px] md:shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl leading-none">{c.part}</span>
                    <span className="inline-block border border-[#a38e65]/60 text-[#8f7a56] text-xs tracking-[0.06em] px-2.5 py-1 font-['Noto_Serif_JP',serif]">{c.time}</span>
                  </div>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mt-3">{c.title}</h4>
                </div>
                <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="III" ja="Recommended For" en="Ideal For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 border-t border-[#e2ddd2]">
            {RECOMMENDS.map((r) => (
              <div key={r} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 sm:px-5 first:sm:pl-0">
                <i className="ri-check-line text-xl text-[#a38e65] shrink-0"></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NavyBand no="IV" ja="Training Outcomes" en="Outcomes" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <img src="/images/generated/jba-p-040-team-exercise.jpg" alt="Participants demonstrating enhanced team management after the training" className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10" />
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {OUTCOMES.map((o, i) => (
              <div key={o.t} className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8 flex flex-col">
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{String(i + 1).padStart(2, '0')}</span>
                  <h4 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.5]">{o.t}</h4>
                </div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.95]">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New variation 2 (large fee + hairline spec table + includes grid). Origin: home IV Fee + home VII table. */}
      <NavyBand no="V" ja="Format & Pricing" en="Details & Fee" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-14 items-start">
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 text-center">
              <div className="font-['Noto_Serif_JP',serif] text-sm text-[#d7b779] tracking-[0.1em] mb-3">Fee (tax included)</div>
              <div className="font-['Playfair_Display',serif] text-[#f8f6f0] text-5xl sm:text-6xl leading-none">¥400,000</div>
              <div className="w-11 h-px bg-[#a38e65] mx-auto mt-6 mb-5"></div>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">3 hours per day for 10-20 participants.</p>
            </div>
            <div>
              <dl className="border-t-2 border-[#1a2942]">
                {SPECS.map((s) => (
                  <div key={s.k} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                    <dt className="sm:w-[180px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{s.k}</dt>
                    <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{s.v}</dd>
                  </div>
                ))}
              </dl>
              <h4 className="jba-display font-medium text-[#1a2942] text-lg mt-10 mb-5">What's Included</h4>
              <ul className="grid sm:grid-cols-2 gap-x-8">
                {INCLUDES.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><i className="ri-check-line text-[#a38e65]"></i><span>{inc}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <NavyBand no="VI" ja="Application Process" en="How to Apply" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <ol className="relative border-l border-[#e2ddd2] ml-5 max-w-4xl">
            {FLOW.map((s, i) => (
              <li key={s.t} className="relative pl-8 sm:pl-10 pb-9 last:pb-0">
                <span className="absolute -left-[19px] top-0 w-9 h-9 rounded-full bg-[#1a2942] text-[#d7b779] flex items-center justify-center font-['Playfair_Display',serif] text-sm border border-[#a38e65]/50">{i + 1}</span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-1.5">{s.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 mt-12 text-center">
            <h4 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">Inquiries &amp; Applications</h4>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] mb-7 max-w-2xl mx-auto leading-[1.9]">For questions or applications regarding Team Leadership Training, please feel free to contact us.</p>
            <a href="/en/contact" className={BTN08_ON_NAVY}>Inquiries &amp; Applications</a>
          </div>
        </div>
      </section>

      <NavyBand no="VII" ja="Related Training Programs" en="Related Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-8">
            {RELATED.map((r) => (
              <a key={r.href} href={r.href} className="group bg-white border border-[#e2ddd2] p-8 flex flex-col hover:border-[#a38e65]/70 transition-colors duration-300">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 group-hover:text-[#8f7a56] transition-colors">{r.label}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9] mb-6">{r.desc}</p>
                <span className={`${TEXT_ARROW} mt-auto self-start`}>View Details <span className="text-[#a38e65]">→</span></span>
              </a>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/en/seminars" className={BTN08_ON_LIGHT}>Back to Seminars &amp; Events</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
